'use client';
// Session — per-network account session for AmanChain.
// One active wallet per browser tab (sessionStorage — dies with the tab, never on disk).
// Every section of the app (mining, send, swap, deploy, stake) operates ONLY on the
// active wallet: each user sees and controls exclusively their own wallet.
import { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { toast } from 'sonner';
import {
  Wallet, Plus, Upload, KeyRound, Eye, EyeOff, RefreshCcw, BadgeCheck,
  Atom, LogOut, UserRound,
} from 'lucide-react';
import { fetchJson, post, fmtAmt, Mono, CopyBtn } from './shared';
import type { Net, WalletT } from './types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

export interface SessionT {
  walletId: string;
  name: string;
  address: string;
  kind?: string;
  password: string;   // held in memory + sessionStorage only — enables one-click signing
}

const KEY = 'aman-session-v1';

function loadAll(): Record<string, SessionT> {
  if (typeof window === 'undefined') return {};
  try { return JSON.parse(sessionStorage.getItem(KEY) ?? '{}'); } catch { return {}; }
}

interface SessionCtx {
  session: SessionT | null;
  isFounder: boolean;
  wallet: WalletT | null;          // live balance data for the active wallet
  login: (s: SessionT) => void;
  logout: () => void;
  openAccount: () => void;
}

const Ctx = createContext<SessionCtx>({
  session: null, isFounder: false, wallet: null,
  login: () => {}, logout: () => {}, openAccount: () => {},
});

export function SessionProvider({ net, founder, children }: {
  net: Net; founder?: string; children: React.ReactNode;
}) {
  const [all, setAll] = useState<Record<string, SessionT>>({});
  const [dialogOpen, setDialogOpen] = useState(false);

  // Hydrate the session store after mount (deferred — keeps SSR markup identical)
  useEffect(() => {
    let alive = true;
    Promise.resolve().then(() => { if (alive) setAll(loadAll()); });
    return () => { alive = false; };
  }, []);

  const session = all[net] ?? null;

  const login = useCallback((s: SessionT) => {
    setAll(prev => {
      const next = { ...prev, [net]: s };
      try { sessionStorage.setItem(KEY, JSON.stringify(next)); } catch { /* storage full — memory only */ }
      return next;
    });
  }, [net]);

  const logout = useCallback(() => {
    setAll(prev => {
      const next = { ...prev };
      delete next[net];
      try { sessionStorage.setItem(KEY, JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
    toast.info('Wallet locked — your session ended on this network');
  }, [net]);

  // Live wallet data for the active session (balances, assets, staking)
  const { data } = useWallets(net);
  const wallets = data?.wallets ?? [];
  const wallet = session ? (wallets.find(w => w.id === session.walletId) ?? null) : null;
  const isFounder = Boolean(session && founder && session.address === founder);

  const value = useMemo<SessionCtx>(() => ({
    session, isFounder, wallet,
    login, logout,
    openAccount: () => setDialogOpen(true),
  }), [session, isFounder, wallet, login, logout]);

  return (
    <Ctx.Provider value={value}>
      {children}
      <AccountDialog net={net} open={dialogOpen} onOpenChange={setDialogOpen} onLogin={login} />
    </Ctx.Provider>
  );
}

export function useSession() {
  return useContext(Ctx);
}

function useWallets(net: Net) {
  const [data, setData] = useState<{ wallets: WalletT[] } | null>(null);
  useEffect(() => {
    let alive = true;
    const tick = () => fetchJson<{ wallets: WalletT[] }>(`/api/wallets?net=${net}`)
      .then(d => { if (alive) setData(d); })
      .catch(() => {});
    tick();
    const iv = setInterval(tick, 5000);
    return () => { alive = false; clearInterval(iv); };
  }, [net]);
  return { data };
}

// ---------- Header account button ----------
export function AccountBar({ net }: { net: Net }) {
  const { session, wallet, openAccount, logout } = useSession();

  if (!session) {
    return (
      <button onClick={openAccount}
        className="inline-flex items-center gap-1.5 rounded-xl bg-emerald-500/15 text-emerald-300 ring-1 ring-emerald-500/40 px-3 py-2 text-xs font-black hover:bg-emerald-500/25 transition">
        <UserRound className="size-4" /> Connect Wallet
      </button>
    );
  }
  return (
    <div className="inline-flex items-center gap-2 rounded-xl ring-1 ring-border bg-secondary/60 pl-2.5 pr-1.5 py-1.5">
      <span className="rounded-lg bg-primary/10 text-primary ring-1 ring-primary/25 p-1"><UserRound className="size-3.5" /></span>
      <div className="text-left leading-tight max-w-[9rem]">
        <div className="text-[0.68rem] font-black truncate">{session.name}</div>
        <Mono className="text-[0.58rem] text-muted-foreground">
          {fmtAmt(wallet?.balance ?? 0, 2)} AMAN
        </Mono>
      </div>
      <button onClick={logout} title="Lock wallet — end this session"
        className="rounded-lg p-1.5 text-muted-foreground hover:text-rose-400 hover:bg-rose-500/10 transition">
        <LogOut className="size-3.5" />
      </button>
    </div>
  );
}

// ---------- Connect prompt for gated sections ----------
export function ConnectPrompt({ what }: { what: string }) {
  const { openAccount } = useSession();
  return (
    <div className="glass p-8 text-center space-y-3">
      <div className="mx-auto rounded-xl bg-primary/10 text-primary ring-1 ring-primary/25 p-3 w-fit"><Wallet className="size-6" /></div>
      <div className="font-black">Connect your wallet to {what}</div>
      <p className="text-xs text-muted-foreground max-w-md mx-auto leading-relaxed">
        Your session stays on this device only — each user sees and controls exclusively their own wallet.
        Create a new hybrid wallet or import one from its seed.
      </p>
      <Button onClick={openAccount} className="gap-2 font-black"><UserRound className="size-4" /> Connect Wallet</Button>
    </div>
  );
}

// ---------- Account dialog: create / import / unlock ----------
function AccountDialog({ net, open, onOpenChange, onLogin }: {
  net: Net; open: boolean; onOpenChange: (v: boolean) => void;
  onLogin: (s: SessionT) => void;
}) {
  const [mode, setMode] = useState<'unlock' | 'create' | 'import'>('unlock');
  const { data } = useWallets(net);
  const wallets = data?.wallets ?? [];

  const finish = (s: SessionT, msg: string) => {
    onLogin(s);
    onOpenChange(false);
    toast.success(msg, { description: `${s.name} is now active on ${net === 'mainnet' ? 'Mainnet' : 'Testnet'}.` });
  };

  return (
    <Dialog open={open} onOpenChange={v => onOpenChange(v)}>
      <DialogContent className="glass sm:max-w-md max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Wallet className="size-5 text-primary" /> Your wallet</DialogTitle>
          <DialogDescription>
            One active wallet per session — mining, swaps, sends and deployments all run under your address only.
          </DialogDescription>
        </DialogHeader>

        <Tabs value={mode} onValueChange={v => setMode(v as 'unlock' | 'create' | 'import')}>
          <TabsList className="grid grid-cols-3 w-full">
            <TabsTrigger value="unlock" className="gap-1 text-[0.68rem]"><KeyRound className="size-3.5" /> Unlock</TabsTrigger>
            <TabsTrigger value="create" className="gap-1 text-[0.68rem]"><Plus className="size-3.5" /> Create</TabsTrigger>
            <TabsTrigger value="import" className="gap-1 text-[0.68rem]"><Upload className="size-3.5" /> Import</TabsTrigger>
          </TabsList>

          <TabsContent value="unlock" className="mt-3">
            <UnlockForm net={net} wallets={wallets} onDone={finish} />
          </TabsContent>
          <TabsContent value="create" className="mt-3">
            <CreateForm net={net} onDone={finish} />
          </TabsContent>
          <TabsContent value="import" className="mt-3">
            <ImportForm net={net} onDone={finish} />
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}

function UnlockForm({ net, wallets, onDone }: {
  net: Net; wallets: WalletT[];
  onDone: (s: SessionT, msg: string) => void;
}) {
  const [walletId, setWalletId] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      const r = await post<{ ok: boolean; id: string; name: string; address: string; kind?: string; error?: string }>(
        `/api/wallets/unlock?net=${net}`, { id: walletId, password });
      if (r.error || !r.ok) { toast.error(r.error ?? 'Wrong password'); return; }
      onDone({ walletId: r.id, name: r.name, address: r.address, kind: r.kind, password }, 'Wallet unlocked');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Unlock failed');
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-3">
      <div>
        <label className="text-xs font-bold mb-1.5 block">Wallet</label>
        <select value={walletId} onChange={e => setWalletId(e.target.value)}
          className="w-full rounded-lg bg-secondary ring-1 ring-border px-3 py-2.5 text-sm">
          <option value="">Select a wallet on this device…</option>
          {wallets.map(w => (
            <option key={w.id} value={w.id}>{w.name}{w.kind === 'founder' ? ' — FOUNDER' : ''}</option>
          ))}
        </select>
      </div>
      <div>
        <label className="text-xs font-bold mb-1.5 block">Password</label>
        <Input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••" autoComplete="off" />
      </div>
      <Button onClick={submit} disabled={busy || !walletId || password.length < 6} className="w-full gap-2 font-black">
        {busy ? <RefreshCcw className="size-4 animate-spin" /> : <KeyRound className="size-4" />}
        Unlock session
      </Button>
      <p className="text-[0.62rem] text-muted-foreground leading-relaxed">
        The node verifies your password against the AES-256-GCM keystore — the seed never leaves the server.
      </p>
    </div>
  );
}

function CreateForm({ net, onDone }: {
  net: Net; onDone: (s: SessionT, msg: string) => void;
}) {
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ id: string; name: string; address: string; seed: string } | null>(null);
  const [revealed, setRevealed] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      const r = await post<{ id: string; name: string; address: string; seed: string; error?: string }>(
        `/api/wallets?net=${net}`, { name, password });
      if (r.error) { toast.error(r.error); return; }
      setResult({ id: r.id, name: r.name, address: r.address, seed: r.seed });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Creation failed');
    } finally { setBusy(false); }
  };

  if (result) {
    return (
      <div className="space-y-3">
        <div className="rounded-lg bg-emerald-500/10 ring-1 ring-emerald-500/30 p-3 flex items-center gap-2 text-sm text-emerald-400 font-bold">
          <BadgeCheck className="size-4" /> Address: <Mono className="text-emerald-300">{result.address.slice(0, 14)}…</Mono>
          <CopyBtn text={result.address} />
        </div>
        <div className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-4">
          <div className="text-xs font-bold text-amber-400 flex items-center gap-1.5 mb-2"><KeyRound className="size-3.5" /> Secret seed — shown only once</div>
          <div className="rounded-md bg-black/40 p-3 border border-amber-500/20">
            <Mono className="text-amber-300 break-all text-[0.7rem] leading-relaxed">{revealed ? result.seed : '•'.repeat(64)}</Mono>
          </div>
          <Button size="sm" variant="secondary" className="mt-2 gap-1.5 text-xs" onClick={() => setRevealed(r => !r)}>
            {revealed ? <EyeOff className="size-3.5" /> : <Eye className="size-3.5" />} {revealed ? 'Hide' : 'Reveal'}
          </Button>
        </div>
        <Button className="w-full font-black" onClick={() => {
          onDone({ walletId: result.id, name: result.name, address: result.address, password }, 'Wallet created — session opened');
        }}>
          I saved the seed — open my session
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div>
        <label className="text-xs font-bold mb-1.5 block">Wallet name</label>
        <Input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. My Main Vault" />
      </div>
      <div>
        <label className="text-xs font-bold mb-1.5 block">Encryption password (min 6 chars)</label>
        <Input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••" autoComplete="new-password" />
      </div>
      <Button onClick={submit} disabled={busy || name.trim().length < 2 || password.length < 6} className="w-full gap-2 font-black">
        {busy ? <RefreshCcw className="size-4 animate-spin" /> : <Atom className="size-4" />}
        Generate hybrid wallet (Ed25519 + ML-DSA-87)
      </Button>
    </div>
  );
}

function ImportForm({ net, onDone }: {
  net: Net; onDone: (s: SessionT, msg: string) => void;
}) {
  const [name, setName] = useState('');
  const [seed, setSeed] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      const r = await post<{ id: string; name: string; address: string; kind?: string; recovered?: boolean; error?: string }>(
        `/api/wallets?net=${net}`, { mode: 'import', name, password, seed: seed.trim() });
      if (r.error) { toast.error(r.error); return; }
      if (r.recovered) {
        // Seed-proof recovery: the node re-encrypted the EXISTING wallet (same
        // identity — a founder stays founder) under the new password.
        onDone({ walletId: r.id, name: r.name, address: r.address, kind: r.kind, password },
          'Access restored — seed proof accepted');
        toast.success('Wallet re-encrypted under your new password', {
          description: 'Same identity restored — balances, history and founder authority are exactly as they were.',
        });
      } else {
        onDone({ walletId: r.id, name: r.name, address: r.address, kind: r.kind, password }, 'Wallet imported — session opened');
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Import failed');
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-3">
      <div>
        <label className="text-xs font-bold mb-1.5 block">Wallet name</label>
        <Input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Restored Vault" />
      </div>
      <div>
        <label className="text-xs font-bold mb-1.5 block">Seed (64 hex characters)</label>
        <Input value={seed} onChange={e => setSeed(e.target.value)} placeholder="xxxxxxxx…" className="mono" />
      </div>
      <div>
        <label className="text-xs font-bold mb-1.5 block">New encryption password (min 6 chars)</label>
        <Input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••" autoComplete="new-password" />
      </div>
      <Button onClick={submit} disabled={busy || name.trim().length < 2 || seed.trim().length !== 64 || password.length < 6} className="w-full gap-2 font-black">
        {busy ? <RefreshCcw className="size-4 animate-spin" /> : <Upload className="size-4" />}
        Restore & open session
      </Button>
      <p className="text-[0.62rem] text-muted-foreground leading-relaxed">
        Both key layers regenerate deterministically from the seed and re-encrypt under the new password.
        If the seed belongs to a wallet that already exists on this node (e.g. the founder wallet), access to that
        exact wallet is restored under your new password — seed proof is the strongest ownership check there is.
      </p>
    </div>
  );
}
